import React, { useEffect, useRef, useState } from "react";
import { getCachedImageUrl } from "lib/mediaCache";

interface Props extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
}

/**
 * Image component that caches thumbnails via the Cache API.
 * Loads via JS fetch (single request path) to avoid double-fetching.
 * Falls back to the original URL if caching is unavailable or on error.
 */
function CachedImage({ src, alt, ...rest }: Props) {
  const [displaySrc, setDisplaySrc] = useState<string>("");
  const blobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    // Revoke previous blob URL before fetching new one (delayed)
    if (blobUrlRef.current) {
      const old = blobUrlRef.current;
      setTimeout(() => URL.revokeObjectURL(old), 3000);
      blobUrlRef.current = null;
    }

    getCachedImageUrl(src).then((url) => {
      if (cancelled) return;
      setDisplaySrc(url);
      if (url.startsWith("blob:")) {
        blobUrlRef.current = url;
      }
    });

    return () => {
      cancelled = true;
      // Delay revoke to let the browser finish any pending loads
      const urlToRevoke = blobUrlRef.current;
      if (urlToRevoke) {
        setTimeout(() => URL.revokeObjectURL(urlToRevoke), 3000);
        blobUrlRef.current = null;
      }
    };
  }, [src]);

  // Render a placeholder <img> with the SAME props (width/height/className/
  // style) while the async cache fetch is in flight, instead of returning
  // `null`. Returning `null` leaves a 0-height gap that snaps to the real
  // image size once `displaySrc` resolves — that jump propagates up to
  // Virtuoso's spacers and yanks the chat viewport on desktop. With this
  // placeholder, the layout slot is reserved from first render: the
  // browser uses the width/height attributes (set by the caller) plus
  // any CSS min-height to compute a stable size that doesn't change when
  // the real src loads.
  if (!displaySrc) return <img alt={alt} {...rest} />;

  return <img src={displaySrc} alt={alt} {...rest} />;
}

export default React.memo(CachedImage);
